import React, { Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useWorkspaceContext } from "./providers/WorkspaceProvider";
import { useFeatureFlagsContext } from "./providers/FeatureFlagProvider";
import { moduleRegistry } from "./modules/moduleRegistry";
import PageLoading from "./components/PageLoading";
// Import core workspace components
import WorkspaceSettings from "./views/WorkspaceSettings";
import WorkspaceUsers from "./views/WorkspaceUsers";
import PageError from "./components/PageError";

// Guarded redirect component that only redirects if not already at target
const SafeRedirect: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();

  // Extract workspace path: /workspace/:id
  const match = location.pathname.match(/^(\/workspace\/[^/]+)/);
  const workspaceBase = match ? match[1] : "";
  const targetPath = `${workspaceBase}/${to}`;

  // Don't redirect if we're already at the target path
  if (
    location.pathname === targetPath ||
    location.pathname.startsWith(targetPath + "/")
  ) {
    return null;
  }

  // Use absolute path to avoid relative navigation issues
  return <Navigate to={targetPath} replace />;
};

const createEmptyContext = () => {
  const emptyContext = (() => ({ default: () => null })) as any;
  emptyContext.keys = () => [];
  return emptyContext;
};

// Ensure webpack includes all module files under views/components (including nested) for lazy loading.
// The production branch must stay as a direct require.context(...) call so webpack can statically extract the context.
const viewsContext =
  process.env.NODE_ENV === "test"
    ? typeof (require as any).context === "function"
      ? (require as any).context(
          "./modules",
          true,
          /(?:views|components)\/.*\.tsx$/,
        )
      : createEmptyContext()
    : (require as any).context(
        "./modules",
        true,
        /(?:views|components)\/.*\.tsx$/,
      );

// Cache for lazy components - created at module scope, not during render
// This is required for compatibility with React.startTransition (v7_startTransition)
const lazyComponentCache = new Map<
  string,
  React.LazyExoticComponent<React.FC>
>();

const getLazyComponent = (componentPath: string) => {
  // Return cached component if available
  if (lazyComponentCache.has(componentPath)) {
    return lazyComponentCache.get(componentPath)!;
  }

  // Convert './modules/bkg/views/Search' -> './bkg/views/Search.tsx'
  const key = componentPath.replace("./modules", ".").concat(".tsx");

  let LazyComponent: React.LazyExoticComponent<React.FC>;

  if (!viewsContext.keys().includes(key)) {
    console.error("Component not found in webpack context:", key);
    LazyComponent = React.lazy(async () => ({
      default: () => <PageError>Component not found</PageError>,
    }));
  } else {
    LazyComponent = React.lazy(async () => {
      const mod = viewsContext(key);
      return { default: mod.default || mod };
    });
  }

  // Cache the lazy component
  lazyComponentCache.set(componentPath, LazyComponent);
  return LazyComponent;
};

// Debug component to show workspace configuration
const WorkspaceDebugInfo: React.FC = () => {
  const { workspace, enabledModules } = useWorkspaceContext();

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f5f5f5",
        margin: "20px",
        borderRadius: "8px",
      }}
    >
      <h3>🔧 Workspace Debug Info</h3>
      <p>
        <strong>Workspace ID:</strong> {workspace?.id}
      </p>
      <p>
        <strong>Workspace Name:</strong> {workspace?.name}
      </p>
      <p>
        <strong>Enabled Modules:</strong> {JSON.stringify(enabledModules)}
      </p>
      <p>
        <strong>Available Modules:</strong>{" "}
        {JSON.stringify(Object.keys(moduleRegistry))}
      </p>
      <p>
        <strong>Module Configs:</strong>
      </p>
      <pre>
        {JSON.stringify(
          Object.fromEntries(
            Object.entries(moduleRegistry).map(([id, config]) => [
              id,
              {
                name: config.name,
                startPage: config.startPage,
                routes: config.routes.map((r) => r.path),
              },
            ]),
          ),
          null,
          2,
        )}
      </pre>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const { enabledModules, loading, isOwner, isAdmin, isEditor, isUser } =
    useWorkspaceContext();
  const { flags, loading: featureFlagsLoading } = useFeatureFlagsContext();

  if (loading || featureFlagsLoading) return <PageLoading />;

  // Function to check if the user has the required role for a route
  const userHasRole = (requiredRole: string | undefined): boolean => {
    if (!requiredRole) return true; // No role required

    switch (requiredRole) {
      case "owner":
        return isOwner;
      case "admin":
        return isAdmin;
      case "editor":
        return isEditor;
      case "user":
        return isUser;
      default:
        return false;
    }
  };

  // Find the first enabled module's start page for default redirect
  const getDefaultStartPage = () => {
    for (const moduleId of enabledModules) {
      const moduleConfig = moduleRegistry[moduleId];

      if (moduleConfig && moduleConfig.startPage) {
        // Check if user has access to the start page
        const startRoute = moduleConfig.routes.find(
          (route) => route.path === moduleConfig.startPage,
        );

        if (
          startRoute &&
          (!startRoute.requiredRole || userHasRole(startRoute.requiredRole))
        ) {
          if (
            !startRoute.requiredFeature ||
            flags?.[startRoute.requiredFeature]
          ) {
            return `${moduleId}/${moduleConfig.startPage}`;
          }

          // The start page is behind a disabled feature flag, so its route is
          // not mounted. Land on the module's first available navigation item.
          const fallbackNav = moduleConfig.navigation?.find(
            (navItem) =>
              userHasRole(navItem.requiredRole) &&
              (!navItem.requiredFeature || flags?.[navItem.requiredFeature]),
          );
          if (fallbackNav) return `${moduleId}/${fallbackNav.path}`;
        }
      }
    }
    return "settings"; // Fallback to settings if no accessible start page found
  };

  const defaultStartPage = getDefaultStartPage();

  return (
    <Routes>
      {/* Auto-redirect to first enabled module's start page */}
      <Route path="" element={<SafeRedirect to={defaultStartPage} />} />

      {/* Debug route - remove this after testing */}
      <Route path="debug" element={<WorkspaceDebugInfo />} />

      {/* Core Routes */}
      <Route path="settings" element={<WorkspaceSettings />} />
      <Route path="users" element={<WorkspaceUsers />} />
      {isAdmin && (
        <Route
          path="integrations"
          element={<Navigate to="../settings#integrations" replace />}
        />
      )}

      {/* Dynamically Generated Module Routes */}
      {enabledModules.map((moduleId) => {
        const moduleConfig = moduleRegistry[moduleId];
        if (!moduleConfig) return null; // Module not found in registry

        const viewRoutes =
          moduleId === "myvault"
            ? moduleConfig.routes.filter(
                (route) =>
                  route.path === "views" || route.path.startsWith("views/"),
              )
            : [];
        const MyVaultViewsRouteLayout =
          viewRoutes.length > 0
            ? getLazyComponent("./modules/myvault/views/MyVaultViewsLayout")
            : null;
        const viewsIndexRoute = viewRoutes.find(
          (route) => route.path === "views",
        );
        const canAccessViewsLayout =
          !viewsIndexRoute?.requiredRole ||
          userHasRole(viewsIndexRoute.requiredRole);
        const routes = moduleConfig.routes.filter(
          (route) => !viewRoutes.includes(route),
        );

        const renderRoute = (route: (typeof moduleConfig.routes)[number]) => {
          // Skip routes where user doesn't have required role or feature is disabled
          if (route.requiredRole && !userHasRole(route.requiredRole))
            return null;
          if (route.requiredFeature && !flags?.[route.requiredFeature])
            return null;

          const LazyComponent = getLazyComponent(route.componentPath);

          return (
            <Route
              key={`${moduleId}-${route.path}`}
              path={`${moduleId}/${route.path}`}
              element={
                <Suspense fallback={<PageLoading />}>
                  <LazyComponent />
                </Suspense>
              }
            />
          );
        };

        return (
          <React.Fragment key={moduleId}>
            {viewRoutes.length > 0 && canAccessViewsLayout && (
              <Route
                path="myvault/views"
                element={
                  <Suspense fallback={<PageLoading />}>
                    {MyVaultViewsRouteLayout && <MyVaultViewsRouteLayout />}
                  </Suspense>
                }
              >
                {viewRoutes.map((route) => {
                  const nestedPath =
                    route.path === "views"
                      ? undefined
                      : route.path.slice("views/".length);
                  if (
                    (route.requiredRole && !userHasRole(route.requiredRole)) ||
                    (route.requiredFeature && !flags?.[route.requiredFeature])
                  ) {
                    return null;
                  }
                  const LazyComponent = getLazyComponent(route.componentPath);
                  return (
                    <Route
                      key={`${moduleId}-${route.path}`}
                      {...(nestedPath ? { path: nestedPath } : { index: true })}
                      element={
                        <Suspense fallback={<PageLoading />}>
                          <LazyComponent />
                        </Suspense>
                      }
                    />
                  );
                })}
              </Route>
            )}
            {routes.map(renderRoute)}
          </React.Fragment>
        );
      })}

      {/* Catch-all route: redirect invalid routes to default start page */}
      <Route path="*" element={<SafeRedirect to={defaultStartPage} />} />
    </Routes>
  );
};

export default AppRoutes;
